import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Seo from "../components/Seo.jsx";

/* Blogs.
   Public SEO surface at /blogs (index) and /blogs/:slug (article). Two
   layouts share the BlogLayout chrome. Articles live in the POSTS array
   below; adding a post is a matter of dropping a new object into it. */

const COMPANY_NAME = "Community Ninja";
const COMPANY_DOMAIN = "communityninja.ai";
const AUTHOR_NAME = "The Community Ninja team";

/* ============================================================
   Article bodies. Plain JSX, written like a human, no em dashes.
   ============================================================ */

function ArticleFindRelevantThreads() {
  return (
    <>
      <p>
        Most advice about finding customers on Reddit ends after the first
        step. Pick a few subreddits, search for your keywords, reply where
        it feels relevant. That sounds reasonable until you actually try
        it and notice that ninety percent of the threads you find are
        either too old to matter, too vague to act on, or written by
        people who would never buy what you sell. The keyword matched.
        The opportunity did not.
      </p>
      <p>
        The gap between a mention and a real opportunity is wider than it
        looks. Closing it is mostly about getting honest with yourself
        about three filters: language, intent, and timing. If a thread
        does not pass all three, it is probably not worth your time, no
        matter how exciting the title looks.
      </p>

      <h3>Start with the words your customers actually use</h3>
      <p>
        Founders tend to search Reddit using the words they would write
        on their own landing page. Customers do not talk that way. They
        do not search for "AI-powered observability platform." They write
        things like "our logs are a mess and grafana keeps timing out."
        The vocabulary of pain rarely matches the vocabulary of product.
      </p>
      <p>
        Before you set up any monitoring, sit down for an hour and make a
        list of how your last ten customers described the problem before
        they had a name for the solution. Use those phrases as your
        starting set. If you do not have ten customers yet, read the
        first reply in any thread that closely resembles the problem you
        solve. Real users always describe the issue in their own words
        before someone arrives with terminology.
      </p>

      <h3>Filter for intent, not just topic</h3>
      <p>
        A post that mentions your category is not the same as a post
        with intent to do something about it. A thread titled "anyone
        else find slack notifications overwhelming" is a venting post.
        A thread titled "what tool do you use to filter slack
        notifications, paid is fine" is an intent post. Both contain the
        same keywords. Only one will pay for anything.
      </p>
      <p>
        Look for verbs and qualifiers that signal action: "looking for,"
        "switching from," "tired of," "paid is fine," "open to," "anyone
        tried," "what do you recommend." A founder asking a question
        with a budget mentioned, even casually, is hours away from
        spending money. A founder asking a question with no budget
        mentioned is months away. A founder making a statement is not on
        the path at all, though they might be in six months.
      </p>

      <h3>Pay attention to timing</h3>
      <p>
        On Reddit, a thread is alive for roughly forty-eight hours.
        After that the original poster has stopped checking, the
        comments are mostly closed in spirit if not in code, and any
        late arrival looks like it is talking to itself. If your
        monitoring shows you a thread from two weeks ago, the question
        you should ask is not "should I reply" but "what would it have
        taken to see this on day one." Build for the second question
        rather than the first.
      </p>

      <h3>Pick subreddits the way you pick a beat</h3>
      <p>
        Big subreddits feel attractive because of the numbers. They are
        also the loudest, most rules-heavy, and most hostile to anything
        that looks like marketing. The smaller, more specific subreddits
        are usually where conversations turn into customers. A subreddit
        with three thousand members where everyone is a small business
        owner running a particular type of shop is worth more than one
        with three hundred thousand members talking about everything in
        your category.
      </p>
      <p>
        A useful exercise: write down the job title or role of your
        ideal customer, then search Reddit for that role. Look at the
        subreddits where they hang out, not the subreddits about the
        category you serve. People talk about their problems in the
        rooms organized around who they are, not the rooms organized
        around the tools they buy.
      </p>

      <h3>Read what the comments do</h3>
      <p>
        A thread can pass all three filters and still be a bad spot to
        engage. Check the comments before you decide. If the top reply
        already solves the problem with a free workaround, your paid
        product is going to land badly. If the comments are friendly
        toward vendors who introduce themselves clearly, that subreddit
        is one to spend time in. If the comments are openly hostile to
        anything that sounds promotional, treat the thread as a
        listening exercise rather than a chance to reply.
      </p>

      <h3>What "relevant" really means</h3>
      <p>
        Relevant is not "matches your keywords." Relevant is "this
        person, in this thread, in this subreddit, would be glad to
        hear from you right now." That definition will reject most of
        the matches you find, which is the point. The win is not
        replying everywhere. The win is replying somewhere that turns
        into a real conversation, and then doing that again, every
        week, for a year.
      </p>
    </>
  );
}

function ArticleWhatMakesAGoodOpportunity() {
  return (
    <>
      <p>
        Spotting a good business opportunity on Reddit is mostly a
        pattern-recognition skill. After looking at a few hundred posts
        across the subreddits your customers live in, you start to feel
        the difference between a thread that will lead somewhere and a
        thread that will not. This piece is an attempt to make that
        feeling explicit so you can hand it to a teammate or to a tool.
      </p>
      <p>
        A good opportunity is rarely one thing. It is usually a stack of
        small signals lining up at the same time. The more boxes a post
        checks, the more confident you can be that engaging will be
        worth your time.
      </p>

      <h3>Specific pain in the wording</h3>
      <p>
        Generic complaints make for poor opportunities. "Marketing is
        hard" tells you nothing. "We spent four months on outbound for
        a B2B fintech product and got two replies, anyone been through
        this with a six-figure ACV" tells you almost everything you
        need to know about who the person is, what they tried, and
        what they want next. Specificity is the closest thing to a
        single-signal indicator that the person is serious.
      </p>

      <h3>The post is recent</h3>
      <p>
        Threads decay fast on Reddit. A post from the last twelve hours
        is in its prime window. A post from the last three days might
        still get a reply read. A post older than a week is usually
        dead, and replying makes you look like you stumbled in from a
        search engine, which you did. Recency is not optional. If your
        process cannot get to a fresh post within a day, the rest of
        the rubric stops mattering.
      </p>

      <h3>The poster has done some thinking already</h3>
      <p>
        A useful post usually contains evidence that the person has
        already wrestled with the problem. They list what they have
        tried. They explain why the obvious answer does not work for
        them. They mention the constraint that is forcing them to ask
        in public. This is gold. It means the person is past the
        general-research stage and is in the get-specific stage, which
        is exactly when a thoughtful, non-promotional reply lands well.
      </p>
      <p>
        The opposite signal is a question with no context. "What is
        the best CRM" is not an opportunity. It is a survey. The
        replies will be a list of names, and yours will be one of many.
      </p>

      <h3>The comments are warm to vendors who introduce themselves</h3>
      <p>
        Different subreddits have different norms about builders
        replying to questions. Some welcome it as long as you disclose
        and lead with help. Some treat any mention of a product as
        spam, even when it is the right answer. Read three or four
        recent comment threads in the same subreddit before you decide
        whether to engage. If founders are routinely getting upvoted
        for friendly, clear introductions, you are probably safe. If
        every vendor reply is downvoted into oblivion, take it as a
        signal to lurk rather than post.
      </p>

      <h3>The solution space in the replies is unclear</h3>
      <p>
        A post with twenty replies that all converge on a single
        well-known answer is a closed conversation. A post with five
        replies that disagree with each other, or where the top reply
        is a free workaround that the original poster says will not
        work, is an open conversation. Open conversations are where
        new tools get tried.
      </p>

      <h3>The poster is reachable</h3>
      <p>
        Click the username. If the account was created two days ago
        and has one post, you are looking at a low-effort fishing
        attempt or, worse, a competitor running a manufactured demand
        play. If the account has years of activity in adjacent
        subreddits, you are looking at a real person with a real
        problem, and that person is much more likely to follow up on a
        DM or a thoughtful reply.
      </p>

      <h3>A simple rubric</h3>
      <p>
        For each thread, score it from zero to one on the six items
        above. Threads that score four or higher are worth a reply.
        Threads that score three are worth saving and checking back
        later. Threads that score two or lower are not worth the time
        it takes to type a comment, no matter how relevant the
        keywords looked. This is the kind of work a tool can do for
        you, but the rubric is the same whether a human or a model is
        applying it.
      </p>

      <h3>What a great opportunity actually looks like</h3>
      <p>
        Picture a post made six hours ago, in a subreddit with three
        thousand members, by a founder who has been active for two
        years. They describe a specific problem in their own words,
        list two products they tried and explain why each fell short,
        and ask what others use. The top comment is a half-answer that
        does not fit their setup. That is the opportunity. Reply with
        a genuine answer that engages with what they tried, mention
        your product briefly and only if it actually fits, and
        understand that whether they buy this month or this year, you
        have just earned a real conversation. Those add up faster than
        any other channel you will find.
      </p>
    </>
  );
}

function ArticleMonitorWithoutBeingSpammy() {
  return (
    <>
      <p>
        Reddit has a long memory and a low tolerance for marketing.
        That combination makes it the highest-leverage channel for
        founders who get it right, and a fast way to burn a brand for
        founders who get it wrong. The line between the two is not
        about how often you post. It is about the ratio between value
        and ask in everything you do.
      </p>
      <p>
        The good news is that staying on the right side of that line
        is not complicated. The bad news is that it takes more
        patience than the average growth playbook tells you to budget
        for. Here is the approach that holds up over time.
      </p>

      <h3>Spam is not volume, it is ratio</h3>
      <p>
        You can post twenty comments in a week and be a valued
        contributor. You can post one comment in a month and be
        flagged as a spammer. The variable is not how often you show
        up but what the average reader thinks you are there for. If
        nine of your ten replies help someone without mentioning your
        product, the tenth reply that does mention it reads as honest.
        If one of your ten replies helps and nine push a link, you are
        the noise everyone learned to tune out.
      </p>
      <p>
        The old nine-to-one rule about communities still holds. Help
        nine times for every one time you promote, and most of the
        time the promotion can be removed entirely and the comment
        still works.
      </p>

      <h3>Be a participant first</h3>
      <p>
        Before your account replies to anything in a subreddit, it
        should have read in that subreddit. Spend a week scrolling
        through the top posts of the last month, then the new posts of
        the last week. Notice the regulars. Notice which kinds of
        replies get upvoted, which get ignored, and which get gently
        pushed back on by the people who run the place. Most of the
        time you will see that the rules listed in the sidebar are the
        small version of the rules that actually apply.
      </p>

      <h3>Pick a posture you can live with</h3>
      <p>
        You can show up on Reddit as a helper, a peer, or a builder.
        A helper answers questions inside their expertise without
        anyone needing to know what they do for work. A peer is
        another founder in the same boat, comparing notes. A builder
        is upfront about what they are working on and shows it when it
        is genuinely useful. All three are fine. The mistake is
        switching between them based on what each thread asks for.
        Pick one posture for an account and stay there. The trust
        compounds.
      </p>

      <h3>Disclose when it matters</h3>
      <p>
        If you are about to mention your own product in a comment,
        write the sentence "I work on this" or "I built this" before
        the link. It costs you nothing and protects you from the
        worst-case outcome, which is a mod posting your reply as an
        example of what not to do. The communities that care will
        appreciate the honesty. The communities that do not care will
        not notice. Either way you are safer.
      </p>

      <h3>Do not link in every reply</h3>
      <p>
        Many of the best replies do not mention your product at all.
        Someone asks how to solve a problem you have thought about
        more than most. You explain how you would think about it. You
        mention two or three approaches, including the one your
        product takes. You do not link. If the person wants to find
        you, they will click your profile. Make the profile easy to
        read, with a one-line bio that explains what you work on, and
        let curiosity do the work that a link would only spoil.
      </p>

      <h3>Match the tone of the room</h3>
      <p>
        Some subreddits write in long, careful paragraphs. Some run on
        short, casual one-liners. Some are heavy on humor. If your
        comments are noticeably more formal or more polished than
        every other reply on the page, you read as an outsider, even
        when you are saying the right thing. The fix is small. Drop
        the corporate cadence. Use the same words and sentence length
        as everyone else.
      </p>

      <h3>Move slowly, especially at first</h3>
      <p>
        A new account that suddenly starts replying frequently in one
        subreddit gets flagged. Spread your replies across days, not
        hours. Reply to threads where you have nothing to gain. Build
        a posting history that looks like a person who happens to
        work on the thing, not a marketing engine that happens to
        have a person attached. This takes weeks. It will not feel
        productive at first. It is.
      </p>

      <h3>Mods are not your enemy</h3>
      <p>
        Most subreddit moderators are volunteers running tight ships
        on top of a full-time job. They are not against builders.
        They are against the hundred founders a week who join the
        community to drop a link and disappear. If you read the rules,
        disclose your affiliation, and contribute genuinely, you will
        find that most mods will leave you alone, and some will
        actively help you when something is borderline. If you are
        unsure whether a post fits, modmail and ask. That single
        message saves you from most of the trouble you can get into.
      </p>

      <h3>Monitoring should make this easier, not harder</h3>
      <p>
        The point of using a tool to monitor Reddit is to find fewer
        but better threads, so you can spend the time you save on
        writing the kind of replies people remember. If your tool is
        flooding you with every keyword match in every subreddit, it
        is pushing you toward the spammy version of the channel. A
        good tool ranks ruthlessly and shows you the handful of posts
        each day that actually deserve a thoughtful reply. The rest
        is silence, which is fine. Silence is what trust sounds like
        early on.
      </p>
    </>
  );
}

function ArticleTurnConversationsIntoOpportunities() {
  return (
    <>
      <p>
        Finding the right Reddit thread is half the work. Turning that
        thread into a qualified opportunity is the other half, and it
        is the half that founders are usually less prepared for.
        Replying well is a craft. Following up well is a process. Both
        need to be in place before any of the threads you find start
        adding up to a real pipeline.
      </p>
      <p>
        The frame that helps most is to stop thinking about Reddit as
        a place to win deals and start thinking about it as the top of
        a funnel. The win in any single thread is not a closed-won
        notification. The win is moving the conversation one step
        forward, in a way that the other person feels good about.
      </p>

      <h3>Decide what "qualified" means for you</h3>
      <p>
        Before you reply to anything, write down a one-sentence
        definition of a qualified opportunity in your context. For a
        B2B SaaS team that might be "a founder or operator at a
        company in our target size range who has expressed a specific
        problem we solve in the last month." For a consumer product
        that might be "a person who has stated that they would pay
        for something like this." If you do not have this written
        down, every thread will look like an opportunity and you will
        end up replying to all of them, which is the surest way to
        burn out and to look spammy at the same time.
      </p>

      <h3>The first reply is a gift, not a pitch</h3>
      <p>
        The single biggest mistake new founders make on Reddit is
        replying to interesting threads with a pitch. Even a soft
        pitch, even a well-written pitch, is the wrong opening move.
        The first reply should answer the person's question as if you
        had never heard of your own product. Spend three sentences on
        what you would do, mention your product in one sentence only
        if it genuinely fits, and stop. If the person is interested,
        they will reply. If not, you have still earned the goodwill
        of the lurkers who read the thread later.
      </p>

      <h3>Watch for the second signal</h3>
      <p>
        Most of the value in a Reddit reply does not show up in the
        thread itself. It shows up in the actions the reader takes
        afterward. A profile click. A DM. A visit to your site. A
        sign-up under the same handle. These signals are the
        difference between a comment that sounded good and a comment
        that moved someone. Watch for them. They are easier to track
        if your profile bio links to your site and your sign-up flow
        captures referral source.
      </p>

      <h3>Introduce yourself after value, not before</h3>
      <p>
        If a thread becomes a back-and-forth conversation and the
        other person is clearly interested, that is the moment to
        introduce yourself properly. Mention that you work on the
        product, that you would be happy to help them try it, and
        give them a way to talk to you off platform if that is easier.
        Do this once per conversation, not in every reply. The pattern
        you want is: help, help, help, offer. Not: offer, help, help,
        offer.
      </p>

      <h3>Moving off platform</h3>
      <p>
        Reddit DMs are fine for short follow-ups. They are not great
        for long sales conversations, and they expire from the
        person's mental inbox quickly. When a thread turns into real
        interest, suggest moving to email and offer your address. Most
        people will take that offer. The ones who do not are usually
        not serious anyway, which is its own form of qualification.
      </p>

      <h3>Capture the conversation outside Reddit</h3>
      <p>
        Reddit is not a CRM, and trying to use it like one will lose
        you opportunities. The moment a thread looks promising, put
        the link, the username, the subreddit, and the date in
        whatever tool you use to track pipeline. Add a one-line note
        about what they said they needed and what you said you would
        do next. Without this, by the time you have engaged with
        thirty threads in a month, you will not remember which ones
        were worth following up on, and the warm ones will go cold.
      </p>

      <h3>Follow up on a human schedule</h3>
      <p>
        Some Reddit conversations convert in days. Most take weeks or
        months. The right follow-up rhythm is closer to how you would
        stay in touch with a friend who casually mentioned a problem
        than how you would run a sales sequence. A week after the
        thread, a friendly DM with one specific thing that might help
        them. A month later, a note if you ship something relevant.
        That is the cadence that feels right to the person on the
        other end. Anything tighter reads as pressure.
      </p>

      <h3>Track what is working</h3>
      <p>
        After a few months, look at the conversations that turned
        into customers and the conversations that did not. Patterns
        appear quickly. You will notice that certain subreddits punch
        above their weight, certain post formats reliably lead to
        real interest, and certain reply styles get more profile
        clicks than others. Double down on what is working and stop
        spending time in the places that look promising but never
        convert. This is the part of the playbook that almost nobody
        does, which is why almost nobody gets compounding results
        from Reddit.
      </p>

      <h3>The long view</h3>
      <p>
        A subreddit is not a list of leads. It is a room of people
        who are going to keep showing up for years. The founders who
        do well on Reddit treat each thread as a small deposit into a
        trust account that pays out over a much longer horizon than
        any single deal. If you show up consistently, help more than
        you sell, and stay in the rooms where your customers live,
        the pipeline takes care of itself. Not in a week. Not in a
        month. But sooner than you think, and longer than any paid
        channel will ever last.
      </p>
    </>
  );
}

function ArticleSimpleGtmWorkflow() {
  return (
    <>
      <p>
        Most go-to-market advice is written for teams of ten with a
        budget for tools, a content calendar, and someone whose only
        job is to track pipeline. Solo founders need something else.
        You need a workflow you can actually run on a tuesday morning,
        in between fixing a bug and answering a support email, that
        still produces the kind of compounding distribution that a
        team would envy.
      </p>
      <p>
        Reddit is one of the few channels where a solo founder can
        outperform a team, because the work that matters most is the
        kind that does not scale by hiring. It is the kind that
        scales by you showing up, in the right places, for long
        enough that the network notices. Here is the simplest
        workflow that holds up over time.
      </p>

      <h3>Step one: pick three subreddits and nothing else</h3>
      <p>
        Resist the urge to start with twenty. Pick three subreddits
        where your ideal customer is likely to spend time, and ignore
        every other community until those three are working. The
        right three usually fall into a pattern: one large subreddit
        about your customer's role, one smaller subreddit about the
        problem you solve, and one adjacent subreddit where people
        complain about the workflow that leads them to need you.
      </p>
      <p>
        Three is enough to give you variety and not so many that you
        cannot keep up. If you cannot maintain a presence in three,
        the problem is not the number. It is the choice. Drop the
        ones that are not working and try different ones.
      </p>

      <h3>Step two: write down what counts as a hit</h3>
      <p>
        Before you start looking at threads, write a one-sentence
        definition of a hit. Something like "a post in the last two
        days from a small business owner who is asking how to
        coordinate inventory across two locations." If you cannot
        write the sentence, you cannot tell your future self what to
        focus on, and you will end up replying to anything that uses
        the words you searched for. The sentence is the entire filter.
      </p>

      <h3>Step three: spend fifteen minutes a day, not two hours a week</h3>
      <p>
        The temptation is to batch the work into one long session on
        a weekend. That does not match how Reddit moves. Threads age
        out in a day. Replying to a four-day-old post reads as a
        crawler stumbling in late. Fifteen minutes a day, on the same
        rough schedule, beats two hours a week by a wide margin. Set
        a reminder if you need to. Most days you will spend ten
        minutes and have nothing to reply to. That is fine. On the
        days something good comes up, you will be there.
      </p>

      <h3>Step four: have a default reply shape ready</h3>
      <p>
        A solo founder cannot afford to reinvent the reply each time.
        Settle on a shape that fits your voice and stick with it. A
        version that works well is: one sentence acknowledging what
        is hard about the problem the person described, two to three
        sentences answering it the way you would for a friend, and
        one short sentence at the end mentioning what you work on if
        and only if it actually fits. No more. The shape forces you
        to lead with help and limits how much you can lean on
        promotion when you are tired.
      </p>

      <h3>Step five: keep a one-page running doc</h3>
      <p>
        Open a single document and use it for everything. Every
        promising thread you reply to gets a line: link, username,
        subreddit, what you offered, and the date. Every DM
        conversation gets a few lines. Every customer that mentions
        Reddit when they sign up gets a star next to the relevant
        line. After a month this document tells you, with no
        analytics setup at all, which subreddits punch above their
        weight, which kinds of posts convert, and which conversations
        deserve a follow-up. A spreadsheet works too. A notes file
        works. The point is single location, low friction.
      </p>

      <h3>Step six: a weekly thirty-minute review</h3>
      <p>
        Once a week, open the running doc and ask three questions.
        Which subreddits gave me a real conversation. Which gave me
        nothing despite repeated effort. Is there a follow-up I owe
        someone from the last seven days. Spend the rest of the
        thirty minutes sending those follow-ups, not on platform but
        in email if you have an address, with one short note that
        references the thread and offers one specific thing. This
        review is the difference between a workflow that compounds
        and one that resets every week.
      </p>

      <h3>Step seven: refresh the subreddits every quarter</h3>
      <p>
        Every three months, look at the running doc and consider
        whether one of your three subreddits should be replaced.
        Communities drift. A subreddit that was lively a year ago can
        slow down. A subreddit you wrote off can become more
        relevant as your product evolves. The goal is to always be
        spending your time in the three most useful rooms, not the
        three you happened to pick at the start.
      </p>

      <h3>What this workflow gives you</h3>
      <p>
        Run this for three months and you will have a list of fifty
        to a hundred real conversations with people who have a
        problem you solve, a small but growing reputation in three
        communities, and a clear sense of which subreddits are worth
        more of your time. Run it for a year and you will have a
        distribution channel that your future, larger team will look
        at and wonder how you built without spending anything on it.
        The trick was that you did not try to do everything. You
        picked the smallest version of the workflow that still
        produced signal, and you ran it on a schedule that fit a real
        life.
      </p>
    </>
  );
}

function ArticleFirstTenUsersWithoutSpamming() {
  return (
    <>
      <p>
        The first ten users are different from every batch of users
        you will ever get after that. They are the only group where
        you can afford to hand-pick each person, write to them one
        at a time, and treat the conversation as more important than
        the conversion. Reddit is one of the best places to find
        them, and one of the worst places to try to shortcut that
        process. The temptation to drop a polished post in a big
        subreddit and ride the upvotes is real. It is also how
        founders end up shadowbanned with three signups and a sour
        taste about the channel.
      </p>
      <p>
        There is a slower way that works. It looks like this.
      </p>

      <h3>Get clear on who the ten are</h3>
      <p>
        Before you write anything, describe the ten people in a
        paragraph. What do they do for work. What is the problem
        that brought them to look for something like yours. What
        would they have tried before they found you. The more
        specific the description, the easier the rest of this gets.
        If you can name three subreddits where this person spends
        time, you have your map. If you can only name one, that is
        also fine. One is more than enough for the first ten.
      </p>

      <h3>Read the subreddit for a week before you post</h3>
      <p>
        For seven days, do nothing in the subreddit except read.
        Notice the regulars. Notice what gets upvoted and what
        falls flat. Notice the unwritten rules: how people open a
        post, what kind of self-promotion is tolerated, whether
        questions get answered or ignored. By the end of the week
        you will have a real sense of the room, and you will be in
        a much better position to write something that does not
        read as an outsider trying to extract value.
      </p>

      <h3>Reply before you ever post</h3>
      <p>
        Your first ten comments in the subreddit should help someone
        else, with no mention of what you are building. Find
        questions you can answer well and answer them well. This is
        not about karma farming. It is about establishing that there
        is a real person attached to your account, with real
        opinions about the kind of problems the subreddit cares
        about. When you eventually post about your own thing, those
        ten comments are the difference between a stranger and a
        familiar face.
      </p>

      <h3>When you post, post as a builder, not a marketer</h3>
      <p>
        The post that works for the first ten users is almost
        never a launch announcement. It is a builder talking about
        a problem they hit, what they tried, and what they ended up
        making for themselves. The product is in the post, but it
        is in service of a story. People upvote stories. They
        ignore announcements. A useful template:
      </p>
      <ul>
        <li>One paragraph on the problem in plain language.</li>
        <li>One paragraph on what you tried first and why it fell short.</li>
        <li>One paragraph on what you ended up building.</li>
        <li>One paragraph on what you are looking for from the community: feedback, beta testers, harsh opinions, anything that is not just sign-ups.</li>
      </ul>
      <p>
        End with a link to the thing, and one sentence about how to
        reach you if a comment is not the right format. Do not use
        the word "revolutionary." Do not use any phrase you would
        not say out loud to a friend in a bar.
      </p>

      <h3>Reply to every comment, fast</h3>
      <p>
        The first hour after a post is when comments arrive. Be
        there. Reply to every comment, including the negative ones.
        Especially the negative ones. The way you handle a tough
        comment tells lurkers more about you than the post itself.
        Thank people who try the product. Ask follow-up questions
        when someone shares a use case. Treat the thread as a
        conversation, not a launch page.
      </p>

      <h3>Move the warm ones off platform</h3>
      <p>
        When a commenter clearly cares, offer to talk. A DM is
        fine. Email is better. A short call is best, if they are up
        for it. The point of the call is not to sell. It is to ask
        five questions about how they currently solve the problem,
        what would make your product useful to them, and what they
        would need to see before they trust it enough to actually
        use it. These calls are where the first ten users come
        from. Each call is also worth more than any other piece of
        research you can do at this stage.
      </p>

      <h3>Do this in two or three subreddits, max</h3>
      <p>
        It is tempting to write the same post in ten different
        subreddits to hit the magic number faster. Do not. Each
        subreddit deserves a post that fits its tone, and posting
        the same thing in many places is the surest way to get
        flagged as a crosspost spammer. Two or three carefully
        chosen subreddits, with posts tailored to each, will get you
        to ten users more reliably than ten subreddits getting the
        same generic post.
      </p>

      <h3>What ten users actually looks like</h3>
      <p>
        Ten users from Reddit, done right, is not ten signups in a
        day. It is something like this. Day one, you post in the
        first subreddit. Three people sign up that night, two of
        them try the product, one writes back the next morning with
        a thoughtful note. You spend three days iterating based on
        their feedback. Day five, you post in the second subreddit
        with a slightly different angle. Two more signups. By the
        end of the first month, you have ten active users, four of
        whom you have talked to on a call, and at least two of whom
        will tell other people. That is the foundation. It does not
        look like growth at first. It is the only kind that lasts.
      </p>

      <h3>The trap to avoid</h3>
      <p>
        The most common mistake at this stage is to mistake silence
        for failure and start crossposting, or worse, mass DMing.
        Both of those will end your access to the channel. If a
        post does not land, treat it as data. Either the subreddit
        is wrong, the angle is wrong, or the product is not yet
        explainable in a paragraph. Each of those is a real signal.
        None of them is solved by spamming. The founders who win
        from Reddit are the ones who can sit with a quiet thread,
        learn from it, and try again next week with something
        better.
      </p>
    </>
  );
}

/* ============================================================
   Post metadata.
   ============================================================ */

const POSTS = [
  {
    slug: "find-reddit-threads-relevant-to-your-product",
    title: "How to find Reddit threads where your product is actually relevant",
    description:
      "A practical guide to filtering Reddit for threads worth your time, using language, intent, and timing instead of raw keyword matches.",
    excerpt:
      "Most keyword matches are not opportunities. Here is a three-filter approach for separating the threads that will lead somewhere from the ones that will not.",
    publishedAt: "2026-05-12",
    readingTime: "9 min read",
    keywords: [
      "reddit marketing",
      "founder discovery",
      "social listening",
      "customer research",
    ],
    body: <ArticleFindRelevantThreads />,
  },
  {
    slug: "what-makes-a-reddit-post-a-good-business-opportunity",
    title: "What makes a Reddit post a good business opportunity?",
    description:
      "Six signals that separate a high-intent Reddit thread from a casual mention, plus a simple rubric you can apply to any post.",
    excerpt:
      "A real opportunity is rarely one thing. It is a stack of signals lining up at the same time. Here is a checklist for spotting them.",
    publishedAt: "2026-05-15",
    readingTime: "8 min read",
    keywords: [
      "reddit leads",
      "lead qualification",
      "social selling",
      "founder marketing",
    ],
    body: <ArticleWhatMakesAGoodOpportunity />,
  },
  {
    slug: "how-to-monitor-reddit-without-being-spammy",
    title: "How to monitor Reddit without becoming spammy",
    description:
      "The difference between a trusted contributor and a spammer on Reddit is ratio, not volume. A practical guide for anyone who wants to engage without burning their brand.",
    excerpt:
      "Spam is not how often you post. It is the ratio of help to ask in everything you do. Here is the long version of how to stay on the right side of the line.",
    publishedAt: "2026-05-19",
    readingTime: "9 min read",
    keywords: [
      "reddit etiquette",
      "community marketing",
      "founder outreach",
      "reddit rules",
    ],
    body: <ArticleMonitorWithoutBeingSpammy />,
  },
  {
    slug: "turn-subreddit-conversations-into-qualified-opportunities",
    title: "How to turn subreddit conversations into qualified opportunities",
    description:
      "From the first reply to the eventual follow-up, a process for turning Reddit threads into a real pipeline of qualified opportunities.",
    excerpt:
      "Finding the right thread is half the work. Following up well is the other half. Here is the playbook that compounds.",
    publishedAt: "2026-05-23",
    readingTime: "10 min read",
    keywords: [
      "reddit pipeline",
      "qualified leads",
      "community-led growth",
      "reddit follow up",
    ],
    body: <ArticleTurnConversationsIntoOpportunities />,
  },
  {
    slug: "simple-reddit-gtm-workflow-for-solo-founders",
    title: "A simple Reddit GTM workflow for solo founders",
    description:
      "A seven-step Reddit go-to-market workflow built for solo founders: small, repeatable, and designed to compound over months without burning out.",
    excerpt:
      "Most GTM advice assumes a team. Here is the smallest workflow that still produces real distribution on Reddit, designed for one person.",
    publishedAt: "2026-05-25",
    readingTime: "9 min read",
    keywords: [
      "solo founder gtm",
      "reddit workflow",
      "founder distribution",
      "bootstrapped marketing",
    ],
    body: <ArticleSimpleGtmWorkflow />,
  },
  {
    slug: "how-to-get-your-first-10-users-from-reddit-without-spamming",
    title: "How to get your first 10 users from Reddit without spamming",
    description:
      "A slow, honest playbook for getting the first ten users from Reddit by reading, helping, and posting like a builder rather than a marketer.",
    excerpt:
      "The first ten users from Reddit do not come from a polished launch post. They come from a week of reading, ten helpful comments, and one honest story.",
    publishedAt: "2026-05-27",
    readingTime: "10 min read",
    keywords: [
      "first users",
      "reddit launch",
      "early customers",
      "founder marketing",
    ],
    body: <ArticleFirstTenUsersWithoutSpamming />,
  },
];

const POSTS_BY_SLUG = POSTS.reduce((acc, post) => {
  acc[post.slug] = post;
  return acc;
}, {});

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

/* ============================================================
   Shared chrome.
   ============================================================ */

function BlogTopBar({ showBack = false }) {
  const navigate = useNavigate();
  return (
    <header className="legal-top">
      {showBack ? (
        <button
          type="button"
          className="legal-back"
          onClick={() => {
            if (window.history.length > 1) navigate(-1);
            else navigate("/blogs");
          }}
          aria-label="Back to blog"
        >
          {window.I?.ArrowLeft ? <window.I.ArrowLeft size={14} /> : null}
          <span>Back</span>
        </button>
      ) : (
        <span aria-hidden="true" />
      )}
      <Link to="/" className="legal-brand" aria-label={`${COMPANY_NAME} home`}>
        <span className="legal-brand-name">{COMPANY_NAME}</span>
      </Link>
    </header>
  );
}

/* ============================================================
   Index page: /blogs
   ============================================================ */

function BlogsScreen() {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sorted = React.useMemo(
    () =>
      [...POSTS].sort(
        (a, b) =>
          new Date(b.publishedAt).getTime() -
          new Date(a.publishedAt).getTime(),
      ),
    [],
  );

  const blogJsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: `${COMPANY_NAME} Blog`,
    url: `https://${COMPANY_DOMAIN}/blogs`,
    description:
      "Field notes on finding customer-discovery signal in public Reddit conversations, written by the Community Ninja team.",
    publisher: {
      "@type": "Organization",
      name: COMPANY_NAME,
      url: `https://${COMPANY_DOMAIN}`,
      logo: `https://${COMPANY_DOMAIN}/assets/icon.png`,
    },
    blogPost: sorted.map((post) => ({
      "@type": "BlogPosting",
      headline: post.title,
      description: post.description,
      datePublished: post.publishedAt,
      url: `https://${COMPANY_DOMAIN}/blogs/${post.slug}`,
      author: { "@type": "Organization", name: AUTHOR_NAME },
    })),
  };

  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: sorted.map((post, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      url: `https://${COMPANY_DOMAIN}/blogs/${post.slug}`,
      name: post.title,
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `https://${COMPANY_DOMAIN}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: `https://${COMPANY_DOMAIN}/blogs`,
      },
    ],
  };

  return (
    <div className="legal-page blog-page screen-enter" data-kind="blogs">
      <Seo
        title="Reddit Monitoring and Customer Discovery Blog"
        description="Field notes on finding the right Reddit threads, engaging without being spammy, and turning subreddit conversations into customer discovery and pipeline."
        keywords={[
          "reddit marketing blog",
          "customer discovery",
          "subreddit marketing",
          "community-led growth",
          "reddit research",
          "social listening blog",
        ]}
        jsonLd={[blogJsonLd, itemListJsonLd, breadcrumbJsonLd]}
      />

      <BlogTopBar />

      <article className="legal-shell">
        <div className="legal-hero">
          <div className="legal-eyebrow">
            <span className="legal-eyebrow-dot" />
            <span>Blog · Field notes on Reddit and customer discovery</span>
          </div>
          <h1 className="legal-title">
            Notes on finding the conversations your customers are already having.
          </h1>
          <p className="legal-lede">
            Short, useful essays on how to find the right Reddit threads,
            engage without being spammy, and turn subreddit conversations
            into customer discovery, research, and pipeline.
          </p>
          <div className="legal-meta">
            <span>{sorted.length} articles</span>
            <span className="legal-meta-sep">·</span>
            <span>Updated <strong>{formatDate(sorted[0].publishedAt)}</strong></span>
          </div>
        </div>

        {sorted.length > 0 && (
          <Link
            to={`/blogs/${sorted[0].slug}`}
            className="blog-featured-card"
          >
            <span className="blog-featured-flag">Latest</span>
            <div className="blog-index-meta">
              <time dateTime={sorted[0].publishedAt}>
                {formatDate(sorted[0].publishedAt)}
              </time>
              <span className="legal-meta-sep">·</span>
              <span>{sorted[0].readingTime}</span>
            </div>
            <h2 className="blog-featured-title">{sorted[0].title}</h2>
            <p className="blog-featured-excerpt">{sorted[0].excerpt}</p>
            {sorted[0].keywords?.length > 0 && (
              <div className="blog-tags">
                {sorted[0].keywords.slice(0, 3).map((k) => (
                  <span key={k} className="blog-tag">{k}</span>
                ))}
              </div>
            )}
            <div className="blog-index-readmore">
              Read article
              {window.I?.ArrowRight ? <window.I.ArrowRight size={12} /> : null}
            </div>
          </Link>
        )}

        {sorted.length > 1 && (
          <div className="blog-section-label">More articles</div>
        )}

        <ul className="blog-index-list" aria-label="Articles">
          {sorted.slice(1).map((post, idx) => (
            <li
              key={post.slug}
              className="blog-index-item"
              style={{ "--i": idx }}
            >
              <Link to={`/blogs/${post.slug}`} className="blog-index-card">
                <div className="blog-index-meta">
                  <time dateTime={post.publishedAt}>
                    {formatDate(post.publishedAt)}
                  </time>
                  <span className="legal-meta-sep">·</span>
                  <span>{post.readingTime}</span>
                </div>
                <h2 className="blog-index-title">{post.title}</h2>
                <p className="blog-index-excerpt">{post.excerpt}</p>
                {post.keywords?.length > 0 && (
                  <div className="blog-tags">
                    {post.keywords.slice(0, 3).map((k) => (
                      <span key={k} className="blog-tag">{k}</span>
                    ))}
                  </div>
                )}
                <div className="blog-index-readmore">
                  Read article
                  {window.I?.ArrowRight ? <window.I.ArrowRight size={12} /> : null}
                </div>
              </Link>
            </li>
          ))}
        </ul>

        <footer className="legal-footer">
          <div className="legal-footer-note">
            Want the same kind of signal in your inbox each morning? That is
            what {COMPANY_NAME} is built to do.
          </div>
          <div className="legal-footer-links">
            <Link to="/" className="legal-footer-link">
              Try {COMPANY_NAME}
              {window.I?.ArrowRight ? <window.I.ArrowRight size={12} /> : null}
            </Link>
          </div>
        </footer>
      </article>
    </div>
  );
}

/* ============================================================
   Article page: /blogs/:slug
   ============================================================ */

function BlogPostInner({ slug }) {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const post = POSTS_BY_SLUG[slug];

  if (!post) {
    return (
      <div className="legal-page blog-page screen-enter" data-kind="blog-missing">
        <Seo title="Article not found" description="That article does not exist." noindex />
        <BlogTopBar showBack />
        <article className="legal-shell">
          <div className="legal-hero">
            <div className="legal-eyebrow">
              <span className="legal-eyebrow-dot" />
              <span>Blog · 404</span>
            </div>
            <h1 className="legal-title">We could not find that article.</h1>
            <p className="legal-lede">
              It may have moved or never existed. Head back to the index for
              the current set of articles.
            </p>
            <div className="legal-footer-links" style={{ marginTop: 16 }}>
              <Link to="/blogs" className="legal-footer-link">
                Back to all articles
                {window.I?.ArrowRight ? <window.I.ArrowRight size={12} /> : null}
              </Link>
            </div>
          </div>
        </article>
      </div>
    );
  }

  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  const postUrl = `https://${COMPANY_DOMAIN}/blogs/${post.slug}`;
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    url: postUrl,
    mainEntityOfPage: { "@type": "WebPage", "@id": postUrl },
    image: [`https://${COMPANY_DOMAIN}/assets/share-image-new.png`],
    author: { "@type": "Organization", name: AUTHOR_NAME },
    publisher: {
      "@type": "Organization",
      name: COMPANY_NAME,
      url: `https://${COMPANY_DOMAIN}`,
      logo: `https://${COMPANY_DOMAIN}/assets/icon.png`,
    },
    keywords: post.keywords?.join(", "),
    articleSection: "Reddit marketing",
    inLanguage: "en-US",
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `https://${COMPANY_DOMAIN}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: `https://${COMPANY_DOMAIN}/blogs`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: post.title,
        item: postUrl,
      },
    ],
  };

  return (
    <div className="legal-page blog-page screen-enter" data-kind="blog-post">
      <Seo
        title={post.title}
        description={post.description}
        type="article"
        publishedAt={post.publishedAt}
        modifiedAt={post.publishedAt}
        author={AUTHOR_NAME}
        keywords={post.keywords}
        jsonLd={[articleJsonLd, breadcrumbJsonLd]}
      />

      <BlogTopBar showBack />

      <article className="legal-shell">
        <div className="legal-hero">
          <div className="legal-eyebrow">
            <span className="legal-eyebrow-dot" />
            <span>Blog · Field notes</span>
          </div>
          <h1 className="legal-title">{post.title}</h1>
          <p className="legal-lede">{post.excerpt}</p>
          <div className="legal-meta">
            <time dateTime={post.publishedAt}>
              {formatDate(post.publishedAt)}
            </time>
            <span className="legal-meta-sep">·</span>
            <span>{post.readingTime}</span>
            <span className="legal-meta-sep">·</span>
            <span>By <strong>{AUTHOR_NAME}</strong></span>
          </div>
          {post.keywords?.length > 0 && (
            <div className="blog-tags blog-tags-post">
              {post.keywords.slice(0, 4).map((k) => (
                <span key={k} className="blog-tag">{k}</span>
              ))}
            </div>
          )}
        </div>

        <div className="legal-body blog-body">{post.body}</div>

        <footer className="legal-footer">
          <div className="legal-footer-note">
            {COMPANY_NAME} turns the ideas in this article into a workflow:
            it watches the subreddits your customers live in, scores each
            new thread against your business profile, and surfaces the
            handful worth a reply.
          </div>
          <div className="legal-footer-links">
            <Link to="/" className="legal-footer-link">
              Try {COMPANY_NAME}
              {window.I?.ArrowRight ? <window.I.ArrowRight size={12} /> : null}
            </Link>
            <Link to="/blogs" className="legal-footer-link legal-footer-link-quiet">
              All articles
            </Link>
          </div>
        </footer>

        {related.length > 0 && (
          <section className="blog-related" aria-label="Related reading">
            <div className="blog-related-label">Keep reading</div>
            <ul className="blog-related-list">
              {related.map((p) => (
                <li key={p.slug}>
                  <Link to={`/blogs/${p.slug}`} className="blog-related-card">
                    <div className="blog-related-meta">
                      {formatDate(p.publishedAt)} · {p.readingTime}
                    </div>
                    <div className="blog-related-title">{p.title}</div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </div>
  );
}

function BlogPostScreen() {
  const { slug } = useParams();
  return <BlogPostInner slug={slug} />;
}

window.BlogsScreen = BlogsScreen;
window.BlogPostScreen = BlogPostScreen;
window.BLOG_SLUGS = POSTS.map((p) => p.slug);
